import type { ConstValues } from '@openx/types/helpers';
import { organizationType } from '@openx/types/organization';
import type { TFunction } from 'i18next';
import type { OrganizationFormData } from './types';

export const dspRestrictionModes = {
  ALLOW: 'ALLOW',
  BLOCK: 'BLOCK',
} as const;
export type DspRestrictionModes = ConstValues<typeof dspRestrictionModes>;

export const allowedCountries = {
  AUSTRALIA: 'AU',
  USA: 'US',
} as const;
export type AllowedCountries = ConstValues<typeof allowedCountries>;

export const allowedCountriesOptions = [allowedCountries.USA, allowedCountries.AUSTRALIA];

export const allowedDomains = {
  OPENX: 'openx.com',
} as const;

export const organizationFields = {
  ALLOWED_COUNTRIES: 'allowed_countries',
  ALLOWED_DOMAINS: 'allowed_domains',
  BIDSTREAM: 'bidstream',
  BIDSTREAM_PLUS: 'bidstream_plus',
  CREATED_DATE: 'created_date',
  CURATED_DEAL_BUYER_DISCOUNTS_ENABLED: 'curated_deal_buyer_discounts_enabled',
  DEFAULT_CPM_CAP: 'default_cpm_cap',
  DEFAULT_FEES_DESCRIPTION: 'default_fees_description',
  DEFAULT_PLATFORM_SHARE_OX_SOURCED: 'default_platform_share_ox_sourced',
  DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED: 'default_platform_share_partner_sourced',
  DEFAULT_SHARE: 'default_share',
  DESCRIPTION: 'description',
  DSP_RESTRICTION: 'dsp_restriction',
  DSP_RESTRICTION_MODE: 'dsp_restriction_mode',
  EXCHANGE_ACCOUNT_NETWORK_ID: 'exchange_account_network_id',
  EXCHANGE_ACCOUNT_PARTNER_ID: 'exchange_account_partner_id',
  EXPIRATION_DATE: 'expires_at',
  FEE_SPLITTING_PARTNER: 'fee_splitting_partner',
  ID: 'id',
  IDENTITY_GRAPH: 'identity_graph',
  IS_OPENX_REVENUE_SHARE_ENABLED: 'is_openx_revenue_share_enabled',
  MAX_CPM_CAP: 'max_cpm_cap',
  MAX_SHARE: 'max_share',
  NAME: 'name',
  NON_VISIBLE_DEALS: 'non_visible_deals',
  OA_MATCH: 'oa_match',
  ORGANIZATION_TYPE: 'organization_type',
  PARENT_ACCOUNT: 'parent_account',
  PARENT_ACCOUNT_ID: 'parent_account.id',
  PARENT_ACCOUNT_NAME: 'parent_account.name',
  RETARGETING_ENABLED: 'retargeting_enabled',
  SELECTED_PARENT_ID: 'selected_parent_id',
  SHOULD_ALLOW_TO_UPDATE_ORGANIZATION_TYPE: 'should_allow_to_update_organization_type',
  SHOULD_DISABLE_CURATED_DEAL_FEES: 'should_disable_curated_deal_fees',
  THIRD_PARTY_PARTNER: 'third_party_partner',
} as const;
export type OrganizationFields = ConstValues<typeof organizationFields>;

export const REVENUE_SHARE_FIELDS = [
  organizationFields.DEFAULT_CPM_CAP,
  organizationFields.DEFAULT_FEES_DESCRIPTION,
  organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED,
  organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED,
  organizationFields.DEFAULT_SHARE,
  organizationFields.MAX_SHARE,
  organizationFields.MAX_CPM_CAP,
] as const;

export const getAllowedCountriesLabels = (t: TFunction) => ({
  [allowedCountries.AUSTRALIA]: t('Australia'),
  [allowedCountries.USA]: t('United States'),
});

export const getOrganizationTypesLabels = (t: TFunction) => ({
  [organizationType.TechPartner]: t('Tech Partner'),
  [organizationType.Buyer]: t('Buyer'),
  [organizationType.Publisher]: t('Publisher'),
  [organizationType.BuyerDev]: t('OpenX - Buyer Dev'),
});

export const organizationTypesOptions = [
  organizationType.TechPartner,
  organizationType.Buyer,
  organizationType.Publisher,
  organizationType.BuyerDev,
];

export const getOrganizationsLabels = (t: TFunction) => ({
  [organizationFields.ALLOWED_COUNTRIES]: t('Allowed Countries'),
  [organizationFields.ALLOWED_DOMAINS]: t('Allowed Domains'),
  [organizationFields.BIDSTREAM]: t('Bidstream'),
  [organizationFields.BIDSTREAM_PLUS]: t('Bidstream Plus'),
  [organizationFields.CREATED_DATE]: t('Date Created'),
  [organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED]: t(
    'Allow setting curated deal buyer discounts by this partner'
  ),
  [organizationFields.DEFAULT_CPM_CAP]: t('Default Fixed CPM'),
  [organizationFields.DEFAULT_FEES_DESCRIPTION]: t('Additional Fee description'),
  [organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED]: t('OpenX Sourced Percent of Media'),
  [organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED]: t('Partner Sourced Percent of Media'),
  [organizationFields.DEFAULT_SHARE]: t('Default Percent of Media'),
  [organizationFields.DESCRIPTION]: t('Description'),
  [organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID]: t('Partner ID'),
  [organizationFields.EXPIRATION_DATE]: t('Expiration Date'),
  [organizationFields.ID]: t('Organization UID'),
  [organizationFields.IDENTITY_GRAPH]: t('Identity Graph'),
  [organizationFields.MAX_CPM_CAP]: t('Max Fixed CPM'),
  [organizationFields.MAX_SHARE]: t('Max Percentage of Media'),
  [organizationFields.NAME]: t('Provider Name'),
  [organizationFields.NON_VISIBLE_DEALS]: t('Allow creating non-visible deals'),
  [organizationFields.OA_MATCH]: t('Identity Graph'),
  [organizationFields.PARENT_ACCOUNT_ID]: t('Parent Account ID'),
  [organizationFields.PARENT_ACCOUNT_NAME]: t('Parent Organization'),
  [organizationFields.THIRD_PARTY_PARTNER]: t('Third-Party Partners'),
  [organizationFields.ORGANIZATION_TYPE]: t('Organization Type'),
  [organizationFields.FEE_SPLITTING_PARTNER]: t('Fee Splitting Partner'),
});

export const getInitialFormState = (selectedParentId?: string): OrganizationFormData => ({
  [organizationFields.ALLOWED_COUNTRIES]: [allowedCountries.USA],
  [organizationFields.ALLOWED_DOMAINS]: [allowedDomains.OPENX],
  [organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED]: false,
  [organizationFields.DESCRIPTION]: '',
  [organizationFields.NAME]: '',
  [organizationFields.NON_VISIBLE_DEALS]: false,
  [organizationFields.PARENT_ACCOUNT]: null,
  [organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID]: '',
  [organizationFields.DEFAULT_CPM_CAP]: '',
  [organizationFields.DEFAULT_FEES_DESCRIPTION]: '',
  [organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED]: '',
  [organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED]: '',
  [organizationFields.DEFAULT_SHARE]: '',
  [organizationFields.MAX_SHARE]: '',
  [organizationFields.MAX_CPM_CAP]: '',
  [organizationFields.ORGANIZATION_TYPE]: organizationType.TechPartner,
  [organizationFields.FEE_SPLITTING_PARTNER]: false,
  [organizationFields.DSP_RESTRICTION]: [],
  [organizationFields.RETARGETING_ENABLED]: false,
  [organizationFields.SELECTED_PARENT_ID]: selectedParentId,
});

export const MAX_ORGANIZATION_LEVEL = 5;
